import React from "react";

import EditIcon from "@uui/Edit05";
import ReplaceIcon from "@uui/Repeat03";
import UndoIcon from "@uui/ReverseLeft";
import RedoIcon from "@uui/ReverseRight";
import { find, first, isEmpty, last } from "lodash/fp";

import Typography from "@mui/material/Typography";

import { VerticalDivider } from "@app/analytics/components/reports/styles";
import useEditReport from "@app/analytics/MeetingReport/hooks/useEditReport";
import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import useSearchStore from "@app/analytics/MeetingReport/stores/search";
import useTranscriptStore from "@app/analytics/MeetingReport/stores/transcript";
import { isBatchedEdit } from "@app/analytics/MeetingReport/utils/editUtils";
import useMeasure from "@app/common/hooks/useMeasure";
import { titleize } from "@app/utils/utils";

import ConfirmationModal from "@rui/ConfirmationModal";
import { Row } from "@rui/Stack";

import { EditMenuContainer, EditMenuButton, UndoRedoButton } from "./styles";

const SPACING_BREAKPOINT = 470; // breakpoint where we'll start smooshing the space between buttons a little
const CONTENT_BREAKPOINT = 540; // breakpoint where we'll change "Save Changes" -> "Save"

const scrollToNode = (node: Element) => {
  node.scrollIntoView({
    behavior: "auto",
    block: "center",
    inline: "center",
  });
};

const redoMutationObserver = new MutationObserver((mutationList) => {
  const node = find(
    (mutation) => Boolean(mutation.addedNodes[0]),
    mutationList.reverse()
  )?.addedNodes[0];

  if (scrollToNode) {
    scrollToNode(node as Element);
  }
  redoMutationObserver.disconnect();
});

const undoMutationObserver = new MutationObserver((mutationList) => {
  const node = find((mutation) => Boolean(mutation.addedNodes[0]), mutationList)
    ?.addedNodes[0];

  if (scrollToNode) {
    scrollToNode(node as Element);
  }
  undoMutationObserver.disconnect();
});

const EditMenu = () => {
  const [containerRef, , { innerWidth }] = useMeasure();

  const {
    editingSection,
    modal,
    undo,
    hasMadeNoEdits,
    redo,
    redoDisabled,
    setModal,
    transcriptEdits,
    transcriptEditHistory,
  } = useEditStore((state) => ({
    editingSection: state.editingSection,
    modal: state.modal,
    undo: state.undo,
    hasMadeNoEdits:
      state.editingSection === "transcript"
        ? isEmpty(state.transcript.edits)
        : isEmpty(state.notes.edits),
    redo: state.redo,
    redoDisabled:
      state.editingSection === "transcript"
        ? isEmpty(state.transcript.editHistory)
        : isEmpty(state.notes.editHistory),
    setModal: state.setModal,
    transcriptEdits: state.transcript.edits,
    transcriptEditHistory: state.transcript.editHistory,
  }));

  const setActiveItem = useTranscriptStore((state) => state.setActiveItem);

  // Ideally we'd be able to set the new active item in a store action, but
  // the edit and transcript stores are separate and we can't call one from the other
  const handleUndo = () => {
    if (editingSection === "transcript") {
      const edit = first(transcriptEdits);

      if (edit) {
        const newActiveTurn = isBatchedEdit(edit)
          ? first(edit.edits)?.after
          : edit.after;
        if (newActiveTurn?.id) {
          setActiveItem({ id: newActiveTurn?.id, type: "paragraph" }, false);
        }
      }
    }

    if (editingSection === "notes") {
      const notesSection = document.querySelector('[data-scroll-id="notes"]');
      if (notesSection) {
        undoMutationObserver.observe(notesSection, {
          childList: true,
          subtree: true,
        });
      }
    }

    undo();
  };

  const handleRedo = () => {
    if (editingSection === "transcript") {
      const edit = first(transcriptEditHistory);

      if (edit) {
        const newActiveTurn = isBatchedEdit(edit)
          ? last(edit.edits)?.after
          : edit.after;
        if (newActiveTurn?.id) {
          setActiveItem({ id: newActiveTurn?.id, type: "paragraph" }, false);
        }
      }
    }

    if (editingSection === "notes") {
      const notesSection = document.querySelector('[data-scroll-id="notes"]');
      if (notesSection) {
        redoMutationObserver.observe(notesSection, {
          childList: true,
          subtree: true,
        });
      }
    }

    redo();
  };

  const { setSearchMode, setSearchTerm } = useSearchStore((state) => ({
    setSearchMode: state.setSearchMode,
    setSearchTerm: state.setSearchTerm,
  }));

  const handleReplace = () => {
    setSearchMode("replace");
    if (editingSection === "notes") {
      // clear any active turn to avoid scrolling to it
      setActiveItem(null, false);
    }

    const selection = window.getSelection();
    if (selection && selection.toString().trim() !== "") {
      setSearchTerm(selection.toString());
    }
  };

  const { onSaveChanges, onCancelChanges, isSaving } = useEditReport();

  const truncateLabels = innerWidth < CONTENT_BREAKPOINT;

  return (
    <>
      <EditMenuContainer variant="apart" ref={containerRef}>
        <Row
          variant="left"
          spacing={1}
          pl={1}
          flexWrap="wrap"
          overflow="hidden"
          height="16px"
        >
          <EditIcon sx={{ fontSize: "16px", color: "shades.neutral.600" }} />
          <Typography variant="bodySecondaryMed" color="shades.neutral.600">
            Editing
            <span className="gte-mobile">
              {editingSection ? ` ${titleize(editingSection)}` : ""}
            </span>
          </Typography>
        </Row>
        <Row
          variant="right"
          spacing={innerWidth < SPACING_BREAKPOINT ? 0.5 : 1}
        >
          <UndoRedoButton disabled={hasMadeNoEdits} onClick={handleUndo}>
            <UndoIcon />
          </UndoRedoButton>
          <UndoRedoButton disabled={redoDisabled} onClick={handleRedo}>
            <RedoIcon />
          </UndoRedoButton>
          <VerticalDivider size="md" />
          <EditMenuButton
            variant="text"
            sx={{ height: "34px" }}
            startIcon={<ReplaceIcon />}
            onClick={handleReplace}
          >
            <span className="gte-mobile">Replace</span>
          </EditMenuButton>
          <VerticalDivider size="md" />
          <EditMenuButton onClick={() => onCancelChanges(false)}>
            Cancel
          </EditMenuButton>
          <EditMenuButton
            variant="contained"
            color="primary"
            loading={isSaving}
            onClick={onSaveChanges}
            disabled={hasMadeNoEdits}
          >
            Save
            <span className={truncateLabels ? "hidden" : undefined}>
              &nbsp;changes
            </span>
          </EditMenuButton>
        </Row>
      </EditMenuContainer>

      <ConfirmationModal
        title="Save Changes?"
        open={modal === "confirm_cancel_changes"}
        onConfirm={onSaveChanges}
        onCancel={() => onCancelChanges(true)}
        onClose={() => setModal(null)}
        confirmProps={{ label: "Save Changes" }}
        cancelProps={{ label: "Leave" }}
        loading={isSaving}
        showHeaderCloseButton
      >
        <Typography variant="bodyPrimaryReg">
          If you leave without saving, your edits will be discarded. Do you want
          to save your changes before exiting?
        </Typography>
      </ConfirmationModal>
    </>
  );
};

export default EditMenu;
